import { Box, Center, Grid, Heading, Input, Spinner, useToast } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { useParams } from "react-router";

import { ThreediModel } from "@nens/threedi/openapi-client";
import { Schematisation, UpdateRevision } from "@nens/threedi/openapi-client";

import SchematisationsSvg from "@/assets/schematisations.svg?url";

import { getSchematisationsApi } from "../../apiConfiguration";
import { ExplainSideColumn } from "../../components/ExplainSideColumn";
import GenerateAndOpenLink from "../../components/GenerateAndOpenLink";
import { GridCard } from "../../components/GridCard";
import ShowTags from "../../components/ShowTags";
import { Toast } from "../../components/Toast";
import { useSetOrApplyQueryOrganisation } from "../../hooks/organisationQueryParam";
import {
  useInvalidateSchematisationModel,
  useRevision,
  useSavedStates,
  useSchematisation,
  useSchematisationModels,
  useSchematisationRevisionRasters,
  useThreedimodelsRasters,
} from "../../queries/schematisations";

import ChangeSchemaOrganisationButton from "./ChangeSchemaOrganisationButton";
import ChooseRevisionButton from "./ChooseRevisionButton";
import ChooseSimulationButton from "./ChooseSimulationButton";
import DeleteRevisionButton from "./DeleteRevisionButton";
import DeleteSchematisationButton from "./DeleteSchematisationButton";
import ModelDetails from "./ModelDetails";
import ModelTasksInProgress from "./ModelTasksInProgress";
import ModelWithoutSuccess from "./ModelWithoutSuccess";
import NoModelYet from "./NoModelYet";
import RasterDownloadLinks from "./RasterDownloadLinks";
import RevisionCard from "./RevisionCard";
import { SchematisationEditable } from "./SchematisationEditable";

type SchematisationsDetailParams = {
  id: string;
  revisionId: string;
};

export type SaveSchematisationDataType = (
  attributename: string,
  value: Schematisation[keyof Schematisation],
  isMetaAttribute?: boolean
) => void;

export type SaveSchematisationRevisionDataType = (
  attributename: string,
  value: Schematisation[keyof Schematisation]
) => void;

export type SaveThreediModelDataType = (
  attributename: string,
  value: ThreediModel[keyof ThreediModel]
) => void;

const SchematisationsDetail: FC = () => {
  useSetOrApplyQueryOrganisation();

  const toast = useToast();

  const showToast = (title: string, status: "error" | "success") => {
    toast({
      render: ({ onClose }) => <Toast {...{ title, status, onClose }} />,
      duration: 9000,
      isClosable: true,
    });
  };

  const queryClient = useQueryClient();

  const schematisationsApi = getSchematisationsApi();

  const { id, revisionId } = useParams<SchematisationsDetailParams>();

  // Parse the IDs with fallback values
  const idNumber = id ? parseInt(id, 10) : NaN;

  // If revisionId is "new", there was no initial revision.
  let revisionIdNumber = NaN;
  if (revisionId === "new") {
    revisionIdNumber = 0;
  } else if (revisionId) {
    revisionIdNumber = parseInt(revisionId, 10);
  }

  // Call all hooks before any conditional returns
  const schematisationsResponse = useSchematisation(idNumber);
  const revisionResponse = useRevision(idNumber, revisionIdNumber);
  const modelsResponse = useSchematisationModels(idNumber, revisionIdNumber);

  const invalidateModel = useInvalidateSchematisationModel(idNumber, revisionIdNumber);

  const schematisation = schematisationsResponse.isSuccess ? schematisationsResponse.data : null;
  const revision = revisionIdNumber && revisionResponse.isSuccess ? revisionResponse.data : null;

  const enabledModels =
    modelsResponse.isSuccess && modelsResponse.data.length
      ? modelsResponse.data.filter((model) => !model.disabled)
      : [];
  const model = enabledModels.length ? enabledModels[0] : null;

  const schematisationRevisionRasterListResponse = useSchematisationRevisionRasters(
    schematisation?.id,
    revision ? revision.id : 0
  );

  const threedimodelsRasterListResponse = useThreedimodelsRasters(
    model?.id ? model.id.toString() : ""
  );

  const schematisationRevisionRasterList = schematisationRevisionRasterListResponse.isSuccess
    ? schematisationRevisionRasterListResponse.data.results
    : [];

  const predefinedSimulationDataRasterList = threedimodelsRasterListResponse.isSuccess
    ? threedimodelsRasterListResponse.data.results
    : [];

  const savedStatesResponse = useSavedStates(model?.id ? model.id.toString() : "");

  const savedStatesList = savedStatesResponse.isSuccess ? savedStatesResponse.data.results : [];

  // Return early if IDs are invalid - all hooks have been called above
  if (isNaN(idNumber) || isNaN(revisionIdNumber)) {
    return (
      <ExplainSideColumn
        imgUrl={SchematisationsSvg}
        imgAltDescription={"Schematisations icon"}
        headerText={"Schematisation"}
        explanationText={"Detail page of (a single revision of) a schematisation."}
        backUrl={"/"}
      >
        <Center width="100%" marginTop="18px">
          Invalid schematisation or revision ID.
        </Center>
      </ExplainSideColumn>
    );
  }

  const saveSchematisationData: SaveSchematisationDataType = async (
    attributename,
    value,
    isMetaAttribute = false
  ) => {
    let data: Schematisation = { name: schematisation?.name ?? "" };

    if (isMetaAttribute) {
      data = { ...data, meta: { [attributename]: value } };
    } else {
      data = { ...data, [attributename]: value } as Schematisation;
    }

    try {
      if (!schematisation) {
        return;
      }
      await schematisationsApi.schematisationsPartialUpdate({
        id: schematisation?.id ?? 0,
        data: data,
      });
      showToast("Schematisation data saved", "success");
      queryClient.refetchQueries({ queryKey: ["schematisation", schematisation?.id] });
      queryClient.refetchQueries({ queryKey: ["schematisations"] });
    } catch (error) {
      showToast("Something went wrong while saving the schematisation", "error");
      console.log(error);
    }
  };

  const saveSchematisationRevisionData: SaveSchematisationRevisionDataType = async (
    attributename,
    value
  ) => {
    let data: UpdateRevision = {};
    data = { ...data, [attributename]: value } as UpdateRevision;
    try {
      if (!revision || !revision.id || !revision.schematisationId) {
        return;
      }
      schematisationsApi.schematisationsRevisionsPartialUpdate({
        id: revision?.id,
        schematisationPk: revision?.schematisationId,
        data: data,
      });
      showToast("Schematisation data saved", "success");
      queryClient.refetchQueries({ queryKey: ["schematisation", schematisation?.id] });
      queryClient.refetchQueries({ queryKey: ["revision", revision?.schematisationId] });
      queryClient.refetchQueries({ queryKey: ["schematisation-revisions", schematisation?.id] });
      queryClient.refetchQueries({ queryKey: ["schematisations"] });
    } catch (error) {
      showToast("Something went wrong while saving the revision", "error");
      console.log(error);
    }
  };

  const saveThreediModelData: SaveThreediModelDataType = async (attributename, value) => {
    if (attributename !== "name") {
      return;
    }
    const data = { name: value } as ThreediModel;
    try {
      if (!model) {
        return;
      }
      await schematisationsApi.threedimodelsPartialUpdate({
        id: model?.id ?? 0,
        data: data,
      });
      showToast("Schematisation data saved", "success");
      queryClient.refetchQueries({ queryKey: ["schematisation", schematisation?.id] });
    } catch (error) {
      showToast("Something went wrong while saving the schematisation", "error");
      console.log(error);
    }
  };

  const modelDeletionCallback = () => {
    invalidateModel();
    queryClient.refetchQueries({ queryKey: ["schematisation", schematisation?.id] });
    queryClient.refetchQueries({ queryKey: ["schematisations"] });
    queryClient.refetchQueries({ queryKey: ["schematisation-revisions", schematisation?.id] });
  };

  return (
    <ExplainSideColumn
      imgUrl={SchematisationsSvg}
      imgAltDescription={"Schematisations icon"}
      headerText={"Schematisation"}
      explanationText={"Detail page of (a single revision of) a schematisation."}
      backUrl={"/"}
    >
      {schematisation ? (
        <Box mt={2} mb={4}>
          <Heading size="xl" color="brand.deep_sea">
            Schematisation "{schematisation.name}" (id {schematisation.id})
          </Heading>

          <Grid gap="1rem" templateColumns="repeat(4, 1fr)" mb="18px">
            <GridCard colSpan={2} title="Name">
              {schematisation.ranaProjectId !== null ? (
                <Input
                  backgroundColor="brand.grey.100"
                  borderWidth="base"
                  borderStyle="solid"
                  borderColor="brand.glacier"
                  borderRadius="base"
                  color="brand.deep_sea"
                  p={4}
                  id="name-field"
                  disabled
                  _disabled={{
                    backgroundColor: "white",
                  }}
                  value={schematisation.name}
                />
              ) : (
                <SchematisationEditable
                  schematisation={schematisation}
                  attributename={"name"}
                  saveSchematisationData={saveSchematisationData}
                />
              )}
            </GridCard>
            <GridCard colSpan={2} title="Created by">
              <Input
                backgroundColor="brand.grey.100"
                borderWidth="base"
                borderStyle="solid"
                borderColor="brand.glacier"
                borderRadius="4px"
                color="brand.deep_sea"
                p={4}
                id="created-by-field"
                disabled
                _disabled={{
                  backgroundColor: "white",
                }}
                value={
                  schematisation.createdByFirstName && schematisation.createdByLastName
                    ? `${schematisation.createdByFirstName} ${schematisation.createdByLastName}`
                    : schematisation.createdBy
                }
              />
            </GridCard>
            <GridCard colSpan={2} title="Description">
              {schematisation ? (
                <SchematisationEditable
                  isMetaAttribute
                  schematisation={schematisation}
                  attributename={"description"}
                  saveSchematisationData={saveSchematisationData}
                />
              ) : null}
            </GridCard>
            <GridCard colSpan={2} title="Tags">
              <ShowTags tags={schematisation.tags} saveData={saveSchematisationData} isEditable />
            </GridCard>
            <GridCard colSpan={2} title="Organisation">
              <ChangeSchemaOrganisationButton
                saveSchematisationData={saveSchematisationData}
                owner={schematisation.owner}
                disabled={schematisation.ranaProjectId !== null}
              ></ChangeSchemaOrganisationButton>
            </GridCard>
            <GridCard colSpan={2} title="Created on">
              <Input
                backgroundColor="brand.grey.100"
                borderWidth="base"
                borderStyle="solid"
                borderColor="brand.glacier"
                borderRadius="4px"
                p={4}
                id="created-on-field"
                disabled
                _disabled={{
                  backgroundColor: "white",
                }}
                value={schematisation.created?.toLocaleString()}
              />
            </GridCard>
            {/* When a schematisation only exists in a project, users should not be able to delete them in the HCC-management app */}
            {schematisation.ranaProjectId === null && (
              <DeleteSchematisationButton schematisation={schematisation} />
            )}
          </Grid>

          <Heading
            size="lg"
            marginTop={8}
            marginBottom={8}
            borderWidth="base"
            borderStyle="solid"
            borderColor="brand.glacier"
            paddingTop={8}
            color="brand.deep_sea"
          >
            Revision #{revision?.number} (id {revision?.id})
            <Box as="span" marginLeft={8}>
              <ChooseRevisionButton schematisation={schematisation} />
            </Box>
          </Heading>
          {revision ? (
            <>
              <RevisionCard model={model} schematisation={schematisation} revision={revision} />
              <Grid gap="1rem" templateColumns="repeat(4, 1fr)">
                <GridCard colSpan={2} title="Commit message">
                  <SchematisationEditable
                    revision={revision}
                    attributename={"commitMessage"}
                    saveSchematisationRevisionData={saveSchematisationRevisionData}
                  />
                </GridCard>

                <GridCard colSpan={2} title={"Simulations"}>
                  <ChooseSimulationButton revisionId={revision.id} />
                </GridCard>
              </Grid>
              <Grid gap="1rem" templateColumns="repeat(4, 1fr)">
                {revision?.sqlite?.file && (
                  <GridCard colSpan={2} title="Schematisation database">
                    <GenerateAndOpenLink
                      filename={revision.sqlite.file.filename || "Schematisation database"}
                      size={revision.sqlite.file?.size || 0}
                      fetchUrl={async () => {
                        if (!revision.id || !schematisation.id) return;
                        return (
                          await schematisationsApi.schematisationsRevisionsSqliteDownload({
                            id: revision.id,
                            schematisationPk: schematisation.id,
                          })
                        ).getUrl;
                      }}
                    />
                  </GridCard>
                )}
                {schematisation &&
                  schematisationRevisionRasterList.length > 0 &&
                  id &&
                  revisionId && (
                    <RasterDownloadLinks
                      id={id}
                      revisionId={revisionId}
                      schematisationRevisionRasters={schematisationRevisionRasterList}
                    />
                  )}
              </Grid>
              {/* When a schematisation only exists in a project, users should not be able to delete them in the HCC-management app */}
              {schematisation.ranaProjectId === null && (
                <DeleteRevisionButton
                  schematisation={schematisation}
                  revision={revision}
                  model={model}
                />
              )}
            </>
          ) : null}
          {revision ? (
            <>
              <Heading
                size="lg"
                my={8}
                paddingTop={8}
                borderWidth="base"
                borderStyle="solid"
                borderColor="brand.glacier"
                color="brand.deep_sea"
              >
                Rana Model of this revision {model ? `(id ${model.id})` : null}
              </Heading>
              <Box>
                <div>
                  {model && model.isValid ? (
                    <Heading size="md" textTransform="uppercase" color="brand.analytic">
                      Model name
                    </Heading>
                  ) : null}
                </div>
                {model && model.isValid ? (
                  <SchematisationEditable
                    threediModel={model ?? undefined}
                    attributename={"name"}
                    saveThreediModelData={saveThreediModelData}
                  />
                ) : null}
              </Box>
              {model ? (
                <>
                  {model.isValid === true && (
                    <ModelDetails callbackWhenModelDeleted={modelDeletionCallback} model={model} />
                  )}
                  {model.isValid === false && (
                    <ModelWithoutSuccess
                      callbackWhenModelDeleted={modelDeletionCallback}
                      model={model}
                    />
                  )}
                  {model.isValid !== true && model.isValid !== false && (
                    <ModelTasksInProgress
                      callbackWhenAllFinished={modelDeletionCallback}
                      model={model}
                    />
                  )}
                </>
              ) : (
                schematisation.id && (
                  <NoModelYet
                    revision={revision}
                    schematisationId={schematisation.id}
                    callbackToLoadModel={invalidateModel}
                  />
                )
              )}
              {(model && predefinedSimulationDataRasterList.length > 0) ||
              (model && savedStatesList.length > 0) ? (
                <>
                  {/* TODO: Known bug that the heading is shown, but the passed predefinedSimulationDataRasterList items are not of the accepted types so nothing is rendered */}
                  <Heading
                    size="lg"
                    my={8}
                    paddingTop={8}
                    borderWidth="base"
                    borderStyle="solid"
                    borderColor="brand.glacier"
                    color="brand.deep_sea"
                  >
                    Predefined simulation data
                  </Heading>
                  <Grid gap="1rem" templateColumns="repeat(4, 1fr)">
                    {id && revisionId && (
                      <RasterDownloadLinks
                        id={id}
                        revisionId={revisionId}
                        schematisationRevisionRasters={predefinedSimulationDataRasterList}
                        onlyInitial
                      />
                    )}
                    {savedStatesList.length > 0 &&
                      savedStatesList.map((savedState) => (
                        <GridCard title={`Saved state: ${savedState.id}`} key={savedState.id}>
                          <Heading
                            size="md"
                            textTransform="uppercase"
                            color="brand.analytic"
                          ></Heading>
                          <div>Name: {savedState.name}</div>
                          <div>Created: {savedState.created?.toLocaleString()}</div>
                          <div>Tags: {savedState.tags}</div>
                          <div>Type: {savedState.type}</div>
                          <div>Time: {savedState.time}</div>
                          <div>Variables: {savedState.variables}</div>
                          <div>Thresholds: {savedState.thresholds}</div>
                        </GridCard>
                      ))}
                  </Grid>
                </>
              ) : null}
            </>
          ) : (
            revisionId === "new" && (
              <GridCard colSpan={2} title="Revisions">
                <p>This schematisation has no revisions.</p>
              </GridCard>
            )
          )}
        </Box>
      ) : (
        <>
          {schematisationsResponse.status === "error" && (
            <Center width="100%" marginTop="18px">
              Schematisation not found. It is possible that it exists but you are not authorized to
              view this schematisation because it belongs to an organisation that you have no access
              rights for it.
            </Center>
          )}
          {schematisationsResponse.status !== "error" && (
            <Center width="100%" height="100%">
              <Spinner size="xl" />
            </Center>
          )}
        </>
      )}
    </ExplainSideColumn>
  );
};

export default SchematisationsDetail;
